import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export const ADMIN_LANGUAGE_STORAGE_KEY = 'admin_language';
export const SUPPORTED_ADMIN_LANGUAGES = ['zh', 'en'];

export const adminTranslations = {
  zh: {
    layout: {
      product: '技能平台管理',
      title: '管理后台',
      visitClient: '访问前台',
      collapse: '收起',
      expand: '展开',
      logout: '退出登录',
      language: '语言',
      nav: {
        dashboard: '数据概览',
        users: '用户管理',
        tasks: '任务管理',
        bids: '报价管理',
        submissions: '投稿管理',
        marketplace: '市场审核',
        reviews: '评价管理',
        reports: '举报处理',
        posts: '帖子管理',
        categories: '板块管理',
        announcements: '公告管理',
        points: '积分管理',
        finance: '财务概览',
        wallets: '钱包管理',
        orders: '订单管理',
        disputes: '订单争议',
        checkoutRequests: '托管申请',
        withdrawals: '提现审核',
        settings: '后台设置',
      },
    },
    login: {
      title: '管理后台',
      subtitle: '技能平台 · Admin',
      email: '邮箱',
      password: '密码',
      passwordPlaceholder: '请输入密码',
      login: '登录',
      loading: '登录中...',
      noAdminPermission: '无管理员权限',
      failed: '登录失败',
      localDefaultEmail: '本地默认管理员邮箱',
    },
    notFound: {
      title: '页面不存在',
      description: '您访问的管理页面不存在或无权访问',
      back: '返回数据概览',
    },
    dialog: {
      confirmTitle: '确认操作',
      promptTitle: '填写备注',
      confirm: '确认',
      cancel: '取消',
    },
    dashboard: {
      title: '数据概览',
      loading: '加载中...',
      empty: '暂无数据',
      stats: {
        users: '注册用户',
        tasks: '发布任务',
        posts: '论坛帖子',
        avgRating: '平均评分',
        bids: '报价总数',
        replies: '回复总数',
      },
      sub: {
        newThisWeek: value => `本周新增 +${value}`,
        openTasks: value => `进行中 ${value}`,
        reviews: value => `共 ${value} 条评价`,
      },
      charts: {
        users14d: '近14天新增用户',
        tasks14d: '近14天新增任务',
      },
      quickActionsTitle: '快捷操作',
      quickActions: {
        users: '管理用户',
        tasks: '管理任务',
        posts: '帖子审核',
        announcements: '发布公告',
        categories: '板块管理',
      },
    },
    announcements: {
      title: '公告管理',
      cardTitle: '发布全站公告',
      description: '公告将以系统消息形式推送给所有注册用户',
      titleLabel: '公告标题 *',
      titlePlaceholder: '例如：平台维护通知',
      contentLabel: '公告内容',
      contentPlaceholder: '详细内容...',
      sending: '发送中...',
      submit: '发布公告',
      failed: '发送失败',
    },
    categories: {
      title: '板块管理',
      create: '新建板块',
      edit: '编辑',
      delete: '删除',
      columns: {
        icon: '图标',
        name: '名称',
        description: '描述',
        sort: '排序',
        actions: '操作',
      },
      modal: {
        createTitle: '新建板块',
        editTitle: '编辑板块',
      },
      iconLabel: '图标 Emoji',
      nameLabel: '板块名称 *',
      descriptionLabel: '板块描述',
      namePlaceholder: '例如：技术交流',
      descriptionPlaceholder: '简短描述...',
      cancel: '取消',
      saving: '保存中...',
      save: '保存',
      deleteConfirm: {
        title: '删除板块',
        message: '确定删除该板块？',
      },
    },
    tasks: {
      title: '任务管理',
      searchPlaceholder: '搜索任务标题...',
      allStatus: '全部状态',
      total: value => `共 ${value} 个任务`,
      loading: '加载中...',
      previous: '上一页',
      next: '下一页',
      page: value => `第 ${value} 页`,
      status: {
        open: '招募中',
        in_progress: '进行中',
        completed: '已完成',
        closed: '已关闭',
      },
      columns: {
        id: 'ID',
        title: '标题',
        category: '分类',
        owner: '发布者',
        budget: '预算',
        bids: '报价数',
        status: '状态',
        createdAt: '发布时间',
        actions: '操作',
      },
      actions: {
        close: '关闭',
        restore: '恢复',
        delete: '删除',
      },
      deleteConfirm: {
        title: '删除任务',
        message: '确定删除该任务？',
      },
    },
    bids: {
      title: '报价管理',
      searchPlaceholder: '搜索任务或报价人...',
      allStatus: '全部状态',
      loading: '加载中...',
      empty: '暂无数据',
      showing: (filtered, total) => `显示 ${filtered} / ${total} 条`,
      status: {
        pending: '待处理',
        accepted: '已接受',
        rejected: '已拒绝',
      },
      stats: {
        total: '总报价',
        pending: '待处理',
        accepted: '已接受',
        rejected: '已拒绝',
      },
      columns: {
        id: 'ID',
        task: '任务',
        bidder: '报价人',
        price: '金额',
        message: '留言',
        status: '状态',
        createdAt: '时间',
        actions: '操作',
      },
      actions: {
        accept: '接受',
        reject: '拒绝',
        delete: '删除',
      },
      confirm: {
        accept: { title: '接受报价', message: '确认接受此报价？' },
        reject: { title: '拒绝报价', message: '确认拒绝此报价？' },
        delete: { title: '删除报价', message: '确认删除此报价？' },
      },
      errors: {
        loadFailed: '报价数据加载失败',
        acceptFailed: '接受报价失败',
        rejectFailed: '拒绝报价失败',
        deleteFailed: '删除报价失败',
      },
    },
    submissions: {
      title: '投稿管理',
      searchPlaceholder: '搜索任务、提交人或内容...',
      allStatus: '全部状态',
      loading: '加载中...',
      empty: '暂无数据',
      showing: (filtered, total) => `显示 ${filtered} / ${total} 条`,
      attachmentsPrefix: '附件',
      status: {
        pending: '待审核',
        approved: '已通过',
        rejected: '已拒绝',
      },
      columns: {
        id: 'ID',
        task: '任务',
        submitter: '提交人',
        content: '内容',
        attachments: '附件',
        status: '状态',
        createdAt: '时间',
        actions: '操作',
      },
      actions: {
        approve: '通过',
        reject: '拒绝',
        delete: '删除',
      },
      confirm: {
        approve: { title: '通过投稿', message: '通过此投稿？' },
        reject: { title: '拒绝投稿', message: '拒绝此投稿？' },
        delete: { title: '删除投稿', message: '删除此投稿？不可恢复！' },
      },
      errors: {
        loadFailed: '投稿数据加载失败',
      },
    },
    posts: {
      title: '帖子管理',
      searchPlaceholder: '搜索帖子标题...',
      total: value => `共 ${value} 个帖子`,
      loading: '加载中...',
      previous: '上一页',
      next: '下一页',
      page: value => `第 ${value} 页`,
      pinnedBadge: '置顶',
      columns: {
        id: 'ID',
        title: '标题',
        category: '板块',
        author: '作者',
        views: '浏览',
        replies: '回复',
        pinned: '置顶',
        createdAt: '发布时间',
        actions: '操作',
      },
      actions: {
        pin: '置顶',
        unpin: '取消置顶',
        delete: '删除',
      },
      deleteConfirm: {
        title: '删除帖子',
        message: '确定删除该帖子及所有回复？',
      },
    },
    reviews: {
      title: '评价管理',
      searchPlaceholder: '搜索评论内容或用户名...',
      loading: '加载中...',
      empty: '暂无数据',
      showing: value => `显示 ${value} 条`,
      stats: {
        total: '总评价数',
        average: '平均分',
        fiveStar: '5 星',
        sensitive: '疑似违规',
      },
      ratingFilter: {
        all: '全部评分',
        five: '5 星',
        four: '4 星',
        three: '3 星',
        two: '2 星',
        one: '1 星',
        sensitive: '仅看敏感词',
      },
      columns: {
        id: 'ID',
        task: '任务',
        reviewer: '评价者',
        reviewee: '被评价',
        rating: '评分',
        comment: '评论内容',
        createdAt: '时间',
        actions: '操作',
      },
      actions: {
        delete: '删除',
      },
      deleteConfirm: {
        title: '删除评价',
        message: '确定删除此评价？此操作不可恢复',
      },
      errors: {
        loadFailed: '评价数据加载失败',
      },
    },
    reports: {
      title: '举报处理',
      searchPlaceholder: '搜索标题、举报人、原因...',
      allStatus: '全部状态',
      allTypes: '全部类型',
      loading: '加载中...',
      empty: '暂无举报',
      showing: value => `显示 ${value} 条`,
      adminNote: '处理备注',
      adminNoteAria: id => `处理备注 ${id}`,
      status: {
        pending: '待处理',
        reviewing: '处理中',
        resolved: '已处理',
        rejected: '已驳回',
      },
      type: {
        service: '技能服务',
        product: '二手商品',
      },
      stats: {
        total: '全部举报',
        pending: '待处理',
        reviewing: '处理中',
        resolved: '已处理',
      },
      columns: {
        id: 'ID',
        type: '类型',
        target: '举报对象',
        reporter: '举报人',
        reason: '原因',
        detail: '说明',
        status: '状态',
        adminNote: '处理备注',
        createdAt: '时间',
        actions: '操作',
      },
      actions: {
        reviewing: '处理中',
        resolved: '已处理',
        rejected: '驳回',
      },
      errors: {
        loadFailed: '举报数据加载失败',
        updateFailed: '举报状态更新失败',
      },
    },
    marketplaceReview: {
      title: '市场内容审核',
      allStatus: '全部状态',
      loading: '加载中...',
      empty: '暂无数据',
      showing: value => `显示 ${value} 条`,
      tabs: {
        services: '技能服务',
        products: '二手商品',
      },
      status: {
        pending: '待审核',
        published: '已发布',
        rejected: '已拒绝',
      },
      columns: {
        id: 'ID',
        title: '标题',
        category: '分类',
        publisher: '发布人',
        priceMethod: '价格/方式',
        description: '说明',
        status: '状态',
        createdAt: '时间',
        actions: '操作',
      },
      actions: {
        publish: '发布',
        reject: '拒绝',
        returnPending: '退回待审',
        delete: '删除',
      },
      deleteConfirm: {
        title: '删除市场内容',
        message: '确认删除？此操作不可恢复。',
      },
      errors: {
        loadFailed: '市场内容加载失败',
      },
    },
    points: {
      title: '积分管理',
      loading: '加载中...',
      searchPlaceholder: '搜索用户...',
      userCount: value => `${value} 位`,
      recentLogs: '最近日志',
      levels: {
        expert: '大神',
        pro: '达人',
        active: '活跃',
        new: '新人',
      },
      stats: {
        users: '用户数',
        totalPoints: '积分总量',
        logs: '积分日志',
      },
      columns: {
        rank: '排名',
        user: '用户',
        email: '邮箱',
        points: '积分',
        level: '等级',
        actions: '操作',
      },
      actions: {
        adjust: '调整',
        cancel: '取消',
        confirm: '确认',
      },
      adjustModal: {
        title: username => `调整 ${username} 的积分`,
        changeLabel: '变化值（正数增加，负数减少）',
        changePlaceholder: '例如 +10 或 -5',
        reasonLabel: '原因',
        reasonPlaceholder: '例如：任务完成奖励',
      },
      errors: {
        loadFailed: '积分数据加载失败',
        requiredFields: '请填写所有字段',
        adjustFailed: '积分调整失败',
      },
    },
    financeDashboard: {
      title: '财务概览',
      loading: '加载中...',
      empty: '暂无数据',
      errorPrefix: '加载失败',
      requestUnit: '笔',
      orderUnit: () => '单',
      cards: {
        totalBalance: '平台总余额',
        totalFrozen: '托管中金额',
        todayRecharge: '今日充值',
        todayOrders: '今日订单',
        escrowOrders: '托管订单',
        completedOrders: '已完成订单',
        refundedOrders: '已退款订单',
        pendingWithdrawals: '待审核提现',
        pendingCheckoutRequests: '待处理托管申请',
      },
      trends: {
        recharge14d: '近 14 天充值走势',
        orders14d: '近 14 天订单走势',
      },
    },
    users: {
      title: '用户管理',
      searchPlaceholder: '搜索用户名 / 邮箱...',
      total: value => `共 ${value} 名用户`,
      loading: '加载中...',
      previous: '上一页',
      next: '下一页',
      page: value => `第 ${value} 页`,
      emptyValue: '-',
      roles: {
        admin: '管理员',
        user: '普通用户',
        banned: '已封禁',
      },
      adminRoles: {
        super_admin: '超级管理员',
        operations: '运营',
        finance: '财务',
        content: '内容审核',
        readonly: '只读',
      },
      verification: {
        verified: '已认证',
        unverified: '未认证',
      },
      columns: {
        id: 'ID',
        username: '用户名',
        email: '邮箱',
        role: '角色',
        adminRole: '后台权限',
        verification: '认证',
        points: '积分',
        tasks: '任务数',
        posts: '帖子数',
        createdAt: '注册时间',
        actions: '操作',
      },
      actions: {
        ban: '封禁',
        unban: '解封',
        demote: '降权',
        setAdmin: '设管理员',
        setVerified: '设为已认证',
        unverify: '取消认证',
        delete: '删除',
      },
      confirm: {
        ban: {
          title: action => `${action}用户`,
          message: action => `确定${action}该用户吗？`,
        },
        role: {
          title: '修改用户角色',
          message: role => `确定将该用户设为${role}吗？`,
        },
        adminRole: {
          title: '修改后台权限',
          message: '确定修改该管理员后台权限吗？',
        },
        verification: {
          title: '修改认证状态',
          message: action => `确定${action}吗？`,
        },
        delete: {
          title: '删除用户',
          message: '确定删除该用户？此操作不可恢复！',
        },
      },
    },
    wallets: {
      title: '钱包管理',
      loading: '加载中...',
      empty: '暂无用户',
      emptyValue: '-',
      searchPlaceholder: '搜索用户名 / 邮箱...',
      search: '搜索',
      stats: {
        users: '用户数',
        totalBalance: '总余额',
        totalFrozen: '托管中',
      },
      columns: {
        id: 'ID',
        user: '用户',
        email: '邮箱',
        balance: '可用余额',
        frozen: '托管中',
        points: '积分',
        createdAt: '注册时间',
        actions: '操作',
      },
      actions: {
        adjust: '调账',
        cancel: '取消',
        confirm: '确认调账',
        processing: '处理中...',
      },
      adjustModal: {
        title: username => `调整 ${username} 的余额`,
        currentBalance: '当前余额',
        amountLabel: '调整金额（元，正数增，负数减）',
        amountPlaceholder: '例如 100 或 -50',
        reasonLabel: '调整原因（必填，会写入审计流水）',
        reasonPlaceholder: '例如：活动奖励',
      },
      errors: {
        requiredFields: '请填写金额和原因',
      },
    },
    orders: {
      title: '订单管理',
      loading: '加载中...',
      empty: '暂无订单',
      emptyValue: '-',
      total: value => `共 ${value} 条`,
      searchPlaceholder: '搜索订单号 / 任务 / 用户名...',
      search: '搜索',
      status: {
        escrow: '托管中',
        completed: '已完成',
        refunded: '已退款',
        disputed: '争议中',
      },
      filters: {
        allStatus: '全部状态',
      },
      columns: {
        orderNo: '订单号',
        task: '任务',
        buyer: '买家',
        seller: '卖家',
        amount: '金额',
        status: '状态',
        createdAt: '创建时间',
        actions: '操作',
      },
      actions: {
        forceRefund: '强制退款',
      },
      forceRefund: {
        promptTitle: orderNo => `强制退款 ${orderNo}`,
        promptMessage: '请填写原因（会写入审计流水）：',
        promptPlaceholder: '请输入退款原因',
        confirmTitle: '确认强制退款',
        confirmMessage: (amount, buyerName) => `确认将 ¥${amount} 退回买家 ${buyerName}？此操作不可撤销。`,
      },
    },
    checkoutRequests: {
      title: '托管付款申请',
      loading: '加载中...',
      empty: '暂无托管付款申请',
      emptyValue: '-',
      summary: (count, amount) => `共 ${count} 条，合计 ¥ ${amount}`,
      status: {
        pending: '待处理',
        processing: '处理中',
        paid: '已支付',
        completed: '已完成',
        cancelled: '已取消',
      },
      filters: {
        allStatus: '全部状态',
      },
      columns: {
        id: 'ID',
        applicant: '申请人',
        project: '项目',
        amount: '金额',
        description: '说明',
        status: '状态',
        createdAt: '创建时间',
        actions: '操作',
      },
      actions: {
        markProcessing: '标记处理中',
        complete: '完成',
        cancel: '取消',
      },
      confirm: {
        title: '更新托管申请状态',
        message: (title, status) => `确认将「${title}」更新为“${status}”？`,
      },
      errors: {
        loadFailed: '托管付款申请加载失败',
      },
    },
    withdrawals: {
      title: '提现审核',
      loading: '加载中...',
      empty: '暂无提现申请',
      emptyValue: '-',
      summary: (count, amount) => `共 ${count} 笔，合计 ¥ ${amount}`,
      status: {
        pending: '待审核',
        approved: '已通过',
        rejected: '已驳回',
        all: '全部',
        paid: '已打款',
        handled: '已处理',
      },
      accountTypes: {
        alipay: '支付宝',
        bank: '银行卡',
      },
      columns: {
        id: 'ID',
        user: '用户',
        amount: '金额',
        account: '收款账户',
        status: '状态',
        createdAt: '申请时间',
        note: '审核备注',
        actions: '操作',
      },
      actions: {
        approve: '通过',
        reject: '驳回',
      },
      approve: {
        promptTitle: '通过提现',
        promptMessage: (amount, username) => `通过 ¥${amount} 提现给 ${username}？备注（可选）：`,
        promptPlaceholder: '填写审核备注，可留空',
        confirmTitle: '确认通过提现',
        confirmMessage: '确认通过提现？请确保已线下打款。',
      },
      reject: {
        promptTitle: '驳回提现',
        promptMessage: '请填写驳回原因（必填）：',
        promptPlaceholder: '请输入驳回原因',
        confirmTitle: '确认驳回提现',
        confirmMessage: amount => `驳回后 ¥${amount} 将原路退回到用户余额。确认？`,
      },
    },
    disputes: {
      title: '订单争议',
      loading: '加载中...',
      empty: '暂无订单争议',
      emptyValue: '-',
      summary: (count, amount) => `共 ${count} 条，合计 ¥ ${amount}`,
      taskFallback: id => `任务 #${id || '-'}`,
      buyerLabel: '买家',
      sellerLabel: '卖家',
      notePlaceholder: '处理说明',
      noteRequired: id => `请先填写 #${id} 的处理说明`,
      resolvedMessage: (id, resolution) => `#${id} 已${resolution}`,
      status: {
        open: '处理中',
        refunded: '已退款',
        released: '已放款',
        closed: '已关闭',
        all: '全部',
      },
      resolution: {
        refund: '退款',
        release: '放款',
        close: '关闭',
      },
      columns: {
        id: 'ID',
        order: '订单',
        parties: '买卖双方',
        amount: '金额',
        reason: '争议说明',
        evidence: '证据',
        status: '状态',
        handling: '处理',
      },
      errors: {
        loadFailed: '争议列表加载失败',
        resolveFailed: '争议处理失败',
      },
    },
    settings: {
      title: '后台设置',
      loading: '加载中...',
      save: '保存',
      saved: '设置已保存',
      defaultConfig: {
        siteDesc: '专业技能交易平台',
        welcomeEmailTemplate: '欢迎加入十字星，请完善个人资料并开始发布或承接需求。',
        passwordResetTemplate: '收到密码重置请求后，管理员会完成身份核验并协助重置密码。',
        notificationDigestTemplate: '这里是你在十字星的最新消息摘要，请登录平台查看详情。',
        smsTestMessage: '短信测试：{{siteName}} 后台短信通道验证',
      },
      tabs: {
        general: '⚙️ 基本信息',
        policy: '🔐 注册策略',
        email: '📧 邮件、短信与密码',
        notifications: '🔔 通知模板',
        system: '🛠 系统维护',
      },
      general: {
        title: '基本信息',
        siteName: '站点名称',
        siteDesc: '站点描述',
        contactEmail: '联系邮箱',
      },
      policy: {
        title: '注册策略',
        regEnabledTitle: '开放注册',
        regEnabledDesc: '允许新用户注册账户',
        guestBrowseTitle: '游客浏览',
        guestBrowseDesc: '允许未登录用户浏览公开内容',
        autoPointsTitle: '自动积分',
        autoPointsDesc: '发帖/回帖/完成任务自动获得积分',
      },
      email: {
        title: '邮件、短信与密码重置',
        description: '管理系统发送的通知模板，并处理用户提交的密码重置请求。',
        emailNotifTitle: '启用邮件通知',
        emailNotifDesc: '给用户发送邮件提醒',
        welcomeTemplate: '欢迎邮件模板',
        passwordResetTemplate: '密码重置模板',
        notificationDigestTemplate: '通知摘要模板',
        smsTestTitle: '短信测试',
        smsStatus: (provider, twilioConfigured) => `Provider：${provider} · Twilio：${twilioConfigured ? '已配置' : '未配置'}`,
        smsStatusNotLoaded: '短信通道状态未加载',
        refreshStatus: '刷新状态',
        refresh: '刷新',
        loading: '加载中...',
        sending: '发送中...',
        sendTest: '发送测试',
        smsSubmitted: '短信测试已提交',
        smsFailed: '短信测试失败',
        resetRequestsTitle: '密码重置请求',
        resetEmpty: '暂无重置请求',
        accountStatus: (username, status, createdAt) => `账号：${username} · 状态：${status} · ${createdAt}`,
        unmatchedAccount: '未匹配',
        resolved: '已处理',
        ignored: '忽略',
        ignoredSaved: '已忽略重置请求',
        resolvedSaved: '已标记为已处理',
      },
      notifications: {
        title: '站内通知模板',
        description: '编辑系统自动通知的中英文标题和正文。变量请保留双花括号格式，例如 {{username}}。',
        loading: '加载中...',
        count: count => `共 ${count} 个模板`,
        refresh: '刷新',
        empty: '暂无可编辑模板',
        variableList: variables => `变量：${(variables || []).map(v => `{{${v}}}`).join('、') || '无'}`,
        overridden: '已覆盖',
        zhTitle: '中文标题',
        zhContent: '中文正文',
        enTitle: '英文标题',
        enContent: '英文正文',
        save: '保存通知模板',
        saved: '通知模板已保存',
      },
      system: {
        title: '系统维护',
        maintenanceMode: '维护模式',
        maintenanceModeDesc: '开启后普通用户无法访问，仅管理员可用',
        backupTitle: '数据备份',
        generating: '生成中...',
        downloadDatabase: '下载数据库',
        exporting: '导出中...',
        exportLogs: '导出日志',
        processing: '处理中...',
        clearCache: '清理缓存',
        backupGenerated: '数据备份已生成',
        logsExported: '审计日志已导出',
        cacheRefreshed: '缓存已刷新',
        dangerTitle: '⚠️ 危险操作',
        dangerDescription: '生产环境不提供页面一键清库。需要执行数据重置时，请先下载备份，再由管理员在服务器侧执行受控脚本。',
        saveConfig: '保存配置',
      },
    },
  },
  en: {
    layout: {
      product: 'Skill Platform Admin',
      title: 'Admin Console',
      visitClient: 'Open Client',
      collapse: 'Collapse',
      expand: 'Expand',
      logout: 'Log out',
      language: 'Language',
      nav: {
        dashboard: 'Dashboard',
        users: 'Users',
        tasks: 'Tasks',
        bids: 'Bids',
        submissions: 'Submissions',
        marketplace: 'Marketplace Review',
        reviews: 'Reviews',
        reports: 'Reports',
        posts: 'Posts',
        categories: 'Categories',
        announcements: 'Announcements',
        points: 'Points',
        finance: 'Finance',
        wallets: 'Wallets',
        orders: 'Orders',
        disputes: 'Disputes',
        checkoutRequests: 'Checkout Requests',
        withdrawals: 'Withdrawals',
        settings: 'Settings',
      },
    },
    login: {
      title: 'Admin Console',
      subtitle: 'Skill Platform · Admin',
      email: 'Email',
      password: 'Password',
      passwordPlaceholder: 'Enter password',
      login: 'Log in',
      loading: 'Logging in...',
      noAdminPermission: 'No admin permission',
      failed: 'Login failed',
      localDefaultEmail: 'Local default admin email',
    },
    notFound: {
      title: 'Page not found',
      description: 'The admin page does not exist or you do not have access.',
      back: 'Back to dashboard',
    },
    dialog: {
      confirmTitle: 'Confirm action',
      promptTitle: 'Add note',
      confirm: 'Confirm',
      cancel: 'Cancel',
    },
    dashboard: {
      title: 'Dashboard',
      loading: 'Loading...',
      empty: 'No data',
      stats: {
        users: 'Registered users',
        tasks: 'Published tasks',
        posts: 'Forum posts',
        avgRating: 'Average rating',
        bids: 'Total bids',
        replies: 'Total replies',
      },
      sub: {
        newThisWeek: value => `+${value} this week`,
        openTasks: value => `${value} in progress`,
        reviews: value => `${value} reviews`,
      },
      charts: {
        users14d: 'New users in 14 days',
        tasks14d: 'New tasks in 14 days',
      },
      quickActionsTitle: 'Quick actions',
      quickActions: {
        users: 'Manage users',
        tasks: 'Manage tasks',
        posts: 'Review posts',
        announcements: 'Publish announcement',
        categories: 'Manage categories',
      },
    },
    announcements: {
      title: 'Announcements',
      cardTitle: 'Publish site-wide announcement',
      description: 'Announcements are pushed to all registered users as system messages.',
      titleLabel: 'Announcement title *',
      titlePlaceholder: 'Example: Platform maintenance notice',
      contentLabel: 'Announcement content',
      contentPlaceholder: 'Detailed content...',
      sending: 'Sending...',
      submit: 'Publish announcement',
      failed: 'Send failed',
    },
    categories: {
      title: 'Categories',
      create: 'New category',
      edit: 'Edit',
      delete: 'Delete',
      columns: {
        icon: 'Icon',
        name: 'Name',
        description: 'Description',
        sort: 'Sort',
        actions: 'Actions',
      },
      modal: {
        createTitle: 'New category',
        editTitle: 'Edit category',
      },
      iconLabel: 'Icon Emoji',
      nameLabel: 'Category name *',
      descriptionLabel: 'Category description',
      namePlaceholder: 'Example: Technical discussion',
      descriptionPlaceholder: 'Short description...',
      cancel: 'Cancel',
      saving: 'Saving...',
      save: 'Save',
      deleteConfirm: {
        title: 'Delete category',
        message: 'Delete this category?',
      },
    },
    tasks: {
      title: 'Tasks',
      searchPlaceholder: 'Search task title...',
      allStatus: 'All statuses',
      total: value => `${value} tasks`,
      loading: 'Loading...',
      previous: 'Previous',
      next: 'Next',
      page: value => `Page ${value}`,
      status: {
        open: 'Open',
        in_progress: 'In progress',
        completed: 'Completed',
        closed: 'Closed',
      },
      columns: {
        id: 'ID',
        title: 'Title',
        category: 'Category',
        owner: 'Owner',
        budget: 'Budget',
        bids: 'Bids',
        status: 'Status',
        createdAt: 'Created',
        actions: 'Actions',
      },
      actions: {
        close: 'Close',
        restore: 'Restore',
        delete: 'Delete',
      },
      deleteConfirm: {
        title: 'Delete task',
        message: 'Delete this task?',
      },
    },
    bids: {
      title: 'Bids',
      searchPlaceholder: 'Search task or bidder...',
      allStatus: 'All statuses',
      loading: 'Loading...',
      empty: 'No data',
      showing: (filtered, total) => `Showing ${filtered} / ${total}`,
      status: {
        pending: 'Pending',
        accepted: 'Accepted',
        rejected: 'Rejected',
      },
      stats: {
        total: 'Total bids',
        pending: 'Pending',
        accepted: 'Accepted',
        rejected: 'Rejected',
      },
      columns: {
        id: 'ID',
        task: 'Task',
        bidder: 'Bidder',
        price: 'Price',
        message: 'Message',
        status: 'Status',
        createdAt: 'Time',
        actions: 'Actions',
      },
      actions: {
        accept: 'Accept',
        reject: 'Reject',
        delete: 'Delete',
      },
      confirm: {
        accept: { title: 'Accept bid', message: 'Accept this bid?' },
        reject: { title: 'Reject bid', message: 'Reject this bid?' },
        delete: { title: 'Delete bid', message: 'Delete this bid?' },
      },
      errors: {
        loadFailed: 'Failed to load bids',
        acceptFailed: 'Failed to accept bid',
        rejectFailed: 'Failed to reject bid',
        deleteFailed: 'Failed to delete bid',
      },
    },
    submissions: {
      title: 'Submissions',
      searchPlaceholder: 'Search task, submitter, or content...',
      allStatus: 'All statuses',
      loading: 'Loading...',
      empty: 'No data',
      showing: (filtered, total) => `Showing ${filtered} / ${total}`,
      attachmentsPrefix: 'Attachment',
      status: {
        pending: 'Pending',
        approved: 'Approved',
        rejected: 'Rejected',
      },
      columns: {
        id: 'ID',
        task: 'Task',
        submitter: 'Submitter',
        content: 'Content',
        attachments: 'Attachments',
        status: 'Status',
        createdAt: 'Time',
        actions: 'Actions',
      },
      actions: {
        approve: 'Approve',
        reject: 'Reject',
        delete: 'Delete',
      },
      confirm: {
        approve: { title: 'Approve submission', message: 'Approve this submission?' },
        reject: { title: 'Reject submission', message: 'Reject this submission?' },
        delete: { title: 'Delete submission', message: 'Delete this submission? This cannot be undone.' },
      },
      errors: {
        loadFailed: 'Failed to load submissions',
      },
    },
    posts: {
      title: 'Posts',
      searchPlaceholder: 'Search post title...',
      total: value => `${value} posts`,
      loading: 'Loading...',
      previous: 'Previous',
      next: 'Next',
      page: value => `Page ${value}`,
      pinnedBadge: 'Pinned',
      columns: {
        id: 'ID',
        title: 'Title',
        category: 'Category',
        author: 'Author',
        views: 'Views',
        replies: 'Replies',
        pinned: 'Pinned',
        createdAt: 'Created',
        actions: 'Actions',
      },
      actions: {
        pin: 'Pin',
        unpin: 'Unpin',
        delete: 'Delete',
      },
      deleteConfirm: {
        title: 'Delete post',
        message: 'Delete this post and all replies?',
      },
    },
    reviews: {
      title: 'Reviews',
      searchPlaceholder: 'Search comment or user...',
      loading: 'Loading...',
      empty: 'No data',
      showing: value => `Showing ${value}`,
      stats: {
        total: 'Total reviews',
        average: 'Average rating',
        fiveStar: '5 stars',
        sensitive: 'Potential violations',
      },
      ratingFilter: {
        all: 'All ratings',
        five: '5 stars',
        four: '4 stars',
        three: '3 stars',
        two: '2 stars',
        one: '1 star',
        sensitive: 'Sensitive only',
      },
      columns: {
        id: 'ID',
        task: 'Task',
        reviewer: 'Reviewer',
        reviewee: 'Reviewee',
        rating: 'Rating',
        comment: 'Comment',
        createdAt: 'Time',
        actions: 'Actions',
      },
      actions: {
        delete: 'Delete',
      },
      deleteConfirm: {
        title: 'Delete review',
        message: 'Delete this review? This cannot be undone.',
      },
      errors: {
        loadFailed: 'Failed to load reviews',
      },
    },
    reports: {
      title: 'Reports',
      searchPlaceholder: 'Search title, reporter, or reason...',
      allStatus: 'All statuses',
      allTypes: 'All types',
      loading: 'Loading...',
      empty: 'No reports',
      showing: value => `Showing ${value}`,
      adminNote: 'Admin note',
      adminNoteAria: id => `Admin note ${id}`,
      status: {
        pending: 'Pending',
        reviewing: 'Reviewing',
        resolved: 'Resolved',
        rejected: 'Rejected',
      },
      type: {
        service: 'Service',
        product: 'Secondhand product',
      },
      stats: {
        total: 'All reports',
        pending: 'Pending',
        reviewing: 'Reviewing',
        resolved: 'Resolved',
      },
      columns: {
        id: 'ID',
        type: 'Type',
        target: 'Target',
        reporter: 'Reporter',
        reason: 'Reason',
        detail: 'Detail',
        status: 'Status',
        adminNote: 'Admin note',
        createdAt: 'Time',
        actions: 'Actions',
      },
      actions: {
        reviewing: 'Reviewing',
        resolved: 'Resolved',
        rejected: 'Reject',
      },
      errors: {
        loadFailed: 'Failed to load reports',
        updateFailed: 'Failed to update report status',
      },
    },
    marketplaceReview: {
      title: 'Marketplace Review',
      allStatus: 'All statuses',
      loading: 'Loading...',
      empty: 'No data',
      showing: value => `Showing ${value}`,
      tabs: {
        services: 'Services',
        products: 'Secondhand products',
      },
      status: {
        pending: 'Pending',
        published: 'Published',
        rejected: 'Rejected',
      },
      columns: {
        id: 'ID',
        title: 'Title',
        category: 'Category',
        publisher: 'Publisher',
        priceMethod: 'Price / Method',
        description: 'Description',
        status: 'Status',
        createdAt: 'Time',
        actions: 'Actions',
      },
      actions: {
        publish: 'Publish',
        reject: 'Reject',
        returnPending: 'Return to pending',
        delete: 'Delete',
      },
      deleteConfirm: {
        title: 'Delete marketplace content',
        message: 'Delete this content? This cannot be undone.',
      },
      errors: {
        loadFailed: 'Failed to load marketplace content',
      },
    },
    points: {
      title: 'Points',
      loading: 'Loading...',
      searchPlaceholder: 'Search users...',
      userCount: value => `${value} users`,
      recentLogs: 'Recent logs',
      levels: {
        expert: 'Expert',
        pro: 'Pro',
        active: 'Active',
        new: 'New',
      },
      stats: {
        users: 'Users',
        totalPoints: 'Total points',
        logs: 'Point logs',
      },
      columns: {
        rank: 'Rank',
        user: 'User',
        email: 'Email',
        points: 'Points',
        level: 'Level',
        actions: 'Actions',
      },
      actions: {
        adjust: 'Adjust',
        cancel: 'Cancel',
        confirm: 'Confirm',
      },
      adjustModal: {
        title: username => `Adjust ${username}'s points`,
        changeLabel: 'Change value (positive adds, negative subtracts)',
        changePlaceholder: 'Example: +10 or -5',
        reasonLabel: 'Reason',
        reasonPlaceholder: 'Example: task completion reward',
      },
      errors: {
        loadFailed: 'Failed to load points data',
        requiredFields: 'Please fill in all fields',
        adjustFailed: 'Failed to adjust points',
      },
    },
    financeDashboard: {
      title: 'Finance',
      loading: 'Loading...',
      empty: 'No data',
      errorPrefix: 'Load failed',
      requestUnit: 'requests',
      orderUnit: value => Number(value) === 1 ? 'order' : 'orders',
      cards: {
        totalBalance: 'Total platform balance',
        totalFrozen: 'Escrowed balance',
        todayRecharge: 'Today recharge',
        todayOrders: 'Today orders',
        escrowOrders: 'Escrow orders',
        completedOrders: 'Completed orders',
        refundedOrders: 'Refunded orders',
        pendingWithdrawals: 'Pending withdrawals',
        pendingCheckoutRequests: 'Pending checkout requests',
      },
      trends: {
        recharge14d: 'Recharge trend in 14 days',
        orders14d: 'Order trend in 14 days',
      },
    },
    users: {
      title: 'Users',
      searchPlaceholder: 'Search username / email...',
      total: value => `${value} users`,
      loading: 'Loading...',
      previous: 'Previous',
      next: 'Next',
      page: value => `Page ${value}`,
      emptyValue: '-',
      roles: {
        admin: 'Admin',
        user: 'User',
        banned: 'Banned',
      },
      adminRoles: {
        super_admin: 'Super admin',
        operations: 'Operations',
        finance: 'Finance',
        content: 'Content review',
        readonly: 'Read only',
      },
      verification: {
        verified: 'Verified',
        unverified: 'Unverified',
      },
      columns: {
        id: 'ID',
        username: 'Username',
        email: 'Email',
        role: 'Role',
        adminRole: 'Admin role',
        verification: 'Verification',
        points: 'Points',
        tasks: 'Tasks',
        posts: 'Posts',
        createdAt: 'Registered',
        actions: 'Actions',
      },
      actions: {
        ban: 'Ban',
        unban: 'Unban',
        demote: 'Demote',
        setAdmin: 'Set admin',
        setVerified: 'Set verified',
        unverify: 'Unverify',
        delete: 'Delete',
      },
      confirm: {
        ban: {
          title: action => `${action} user`,
          message: action => `${action} this user?`,
        },
        role: {
          title: 'Change user role',
          message: role => `Set this user as ${role}?`,
        },
        adminRole: {
          title: 'Change admin role',
          message: 'Change this admin permission group?',
        },
        verification: {
          title: 'Change verification status',
          message: action => `${action}?`,
        },
        delete: {
          title: 'Delete user',
          message: 'Delete this user? This cannot be undone.',
        },
      },
    },
    wallets: {
      title: 'Wallets',
      loading: 'Loading...',
      empty: 'No users',
      emptyValue: '-',
      searchPlaceholder: 'Search username / email...',
      search: 'Search',
      stats: {
        users: 'Users',
        totalBalance: 'Total balance',
        totalFrozen: 'Escrowed',
      },
      columns: {
        id: 'ID',
        user: 'User',
        email: 'Email',
        balance: 'Available balance',
        frozen: 'Escrowed',
        points: 'Points',
        createdAt: 'Registered',
        actions: 'Actions',
      },
      actions: {
        adjust: 'Adjust',
        cancel: 'Cancel',
        confirm: 'Confirm adjustment',
        processing: 'Processing...',
      },
      adjustModal: {
        title: username => `Adjust ${username}'s balance`,
        currentBalance: 'Current balance',
        amountLabel: 'Adjustment amount (CNY, positive adds, negative subtracts)',
        amountPlaceholder: 'Example: 100 or -50',
        reasonLabel: 'Adjustment reason (required, written to audit log)',
        reasonPlaceholder: 'Example: campaign reward',
      },
      errors: {
        requiredFields: 'Please enter an amount and reason',
      },
    },
    orders: {
      title: 'Orders',
      loading: 'Loading...',
      empty: 'No orders',
      emptyValue: '-',
      total: value => `${value} orders`,
      searchPlaceholder: 'Search order no. / task / username...',
      search: 'Search',
      status: {
        escrow: 'Escrowed',
        completed: 'Completed',
        refunded: 'Refunded',
        disputed: 'Disputed',
      },
      filters: {
        allStatus: 'All statuses',
      },
      columns: {
        orderNo: 'Order no.',
        task: 'Task',
        buyer: 'Buyer',
        seller: 'Seller',
        amount: 'Amount',
        status: 'Status',
        createdAt: 'Created',
        actions: 'Actions',
      },
      actions: {
        forceRefund: 'Force refund',
      },
      forceRefund: {
        promptTitle: orderNo => `Force refund ${orderNo}`,
        promptMessage: 'Enter a reason. It will be written to the audit log:',
        promptPlaceholder: 'Enter refund reason',
        confirmTitle: 'Confirm force refund',
        confirmMessage: (amount, buyerName) => `Refund ¥${amount} to buyer ${buyerName}? This cannot be undone.`,
      },
    },
    checkoutRequests: {
      title: 'Checkout Requests',
      loading: 'Loading...',
      empty: 'No checkout requests',
      emptyValue: '-',
      summary: (count, amount) => `${count} requests, total ¥ ${amount}`,
      status: {
        pending: 'Pending',
        processing: 'Processing',
        paid: 'Paid',
        completed: 'Completed',
        cancelled: 'Cancelled',
      },
      filters: {
        allStatus: 'All statuses',
      },
      columns: {
        id: 'ID',
        applicant: 'Applicant',
        project: 'Project',
        amount: 'Amount',
        description: 'Description',
        status: 'Status',
        createdAt: 'Created',
        actions: 'Actions',
      },
      actions: {
        markProcessing: 'Mark processing',
        complete: 'Complete',
        cancel: 'Cancel',
      },
      confirm: {
        title: 'Update checkout request status',
        message: (title, status) => `Update "${title}" to "${status}"?`,
      },
      errors: {
        loadFailed: 'Failed to load checkout requests',
      },
    },
    withdrawals: {
      title: 'Withdrawals',
      loading: 'Loading...',
      empty: 'No withdrawal requests',
      emptyValue: '-',
      summary: (count, amount) => `${count} requests, total ¥ ${amount}`,
      status: {
        pending: 'Pending',
        approved: 'Approved',
        rejected: 'Rejected',
        all: 'All',
        paid: 'Paid',
        handled: 'Handled',
      },
      accountTypes: {
        alipay: 'Alipay',
        bank: 'Bank card',
      },
      columns: {
        id: 'ID',
        user: 'User',
        amount: 'Amount',
        account: 'Receiving account',
        status: 'Status',
        createdAt: 'Requested',
        note: 'Review note',
        actions: 'Actions',
      },
      actions: {
        approve: 'Approve',
        reject: 'Reject',
      },
      approve: {
        promptTitle: 'Approve withdrawal',
        promptMessage: (amount, username) => `Approve ¥${amount} withdrawal for ${username}? Note (optional):`,
        promptPlaceholder: 'Enter review note, optional',
        confirmTitle: 'Confirm withdrawal approval',
        confirmMessage: 'Confirm approval? Make sure the offline transfer has been completed.',
      },
      reject: {
        promptTitle: 'Reject withdrawal',
        promptMessage: 'Enter rejection reason (required):',
        promptPlaceholder: 'Enter rejection reason',
        confirmTitle: 'Confirm withdrawal rejection',
        confirmMessage: amount => `Rejecting will return ¥${amount} to the user's balance. Continue?`,
      },
    },
    disputes: {
      title: 'Disputes',
      loading: 'Loading...',
      empty: 'No order disputes',
      emptyValue: '-',
      summary: (count, amount) => `${count} disputes, total ¥ ${amount}`,
      taskFallback: id => `Task #${id || '-'}`,
      buyerLabel: 'Buyer',
      sellerLabel: 'Seller',
      notePlaceholder: 'Handling note',
      noteRequired: id => `Enter a handling note for #${id} first`,
      resolvedMessage: (id, resolution) => `#${id} resolved: ${resolution}`,
      status: {
        open: 'Open',
        refunded: 'Refunded',
        released: 'Released',
        closed: 'Closed',
        all: 'All',
      },
      resolution: {
        refund: 'Refund',
        release: 'Release',
        close: 'Close',
      },
      columns: {
        id: 'ID',
        order: 'Order',
        parties: 'Parties',
        amount: 'Amount',
        reason: 'Dispute reason',
        evidence: 'Evidence',
        status: 'Status',
        handling: 'Handling',
      },
      errors: {
        loadFailed: 'Failed to load disputes',
        resolveFailed: 'Failed to resolve dispute',
      },
    },
    settings: {
      title: 'Settings',
      loading: 'Loading...',
      save: 'Save',
      saved: 'Settings saved',
      defaultConfig: {
        siteDesc: 'Professional skills marketplace',
        welcomeEmailTemplate: 'Welcome to MyriadSkills. Complete your profile and start posting or accepting requests.',
        passwordResetTemplate: 'After a password reset request is received, an administrator will verify identity and help reset the password.',
        notificationDigestTemplate: 'Here is your latest MyriadSkills notification digest. Sign in to view details.',
        smsTestMessage: 'SMS test: {{siteName}} admin SMS channel verification',
      },
      tabs: {
        general: '⚙️ General',
        policy: '🔐 Registration',
        email: '📧 Email, SMS and Password',
        notifications: '🔔 Notification Templates',
        system: '🛠 System Maintenance',
      },
      general: {
        title: 'General',
        siteName: 'Site name',
        siteDesc: 'Site description',
        contactEmail: 'Contact email',
      },
      policy: {
        title: 'Registration Policy',
        regEnabledTitle: 'Open registration',
        regEnabledDesc: 'Allow new users to register accounts',
        guestBrowseTitle: 'Guest browsing',
        guestBrowseDesc: 'Allow signed-out users to browse public content',
        autoPointsTitle: 'Automatic points',
        autoPointsDesc: 'Automatically grant points for posts, replies, and completed tasks',
      },
      email: {
        title: 'Email, SMS and Password Reset',
        description: 'Manage notification templates sent by the admin system and handle password reset requests submitted by users.',
        emailNotifTitle: 'Enable email notifications',
        emailNotifDesc: 'Send email reminders to users',
        welcomeTemplate: 'Welcome email template',
        passwordResetTemplate: 'Password reset template',
        notificationDigestTemplate: 'Notification digest template',
        smsTestTitle: 'SMS test',
        smsStatus: (provider, twilioConfigured) => `Provider: ${provider} · Twilio: ${twilioConfigured ? 'Configured' : 'Not configured'}`,
        smsStatusNotLoaded: 'SMS channel status not loaded',
        refreshStatus: 'Refresh status',
        refresh: 'Refresh',
        loading: 'Loading...',
        sending: 'Sending...',
        sendTest: 'Send test',
        smsSubmitted: 'SMS test submitted',
        smsFailed: 'SMS test failed',
        resetRequestsTitle: 'Password reset requests',
        resetEmpty: 'No reset requests',
        accountStatus: (username, status, createdAt) => `Account: ${username} · Status: ${status} · ${createdAt}`,
        unmatchedAccount: 'Unmatched',
        resolved: 'Resolved',
        ignored: 'Ignore',
        ignoredSaved: 'Reset request ignored',
        resolvedSaved: 'Marked as resolved',
      },
      notifications: {
        title: 'In-App Notification Templates',
        description: 'Edit the Chinese and English titles and bodies for automatic system notifications. Keep variables in double braces, for example {{username}}.',
        loading: 'Loading...',
        count: count => `${count} templates`,
        refresh: 'Refresh',
        empty: 'No editable templates',
        variableList: variables => `Variables: ${(variables || []).map(v => `{{${v}}}`).join(', ') || 'None'}`,
        overridden: 'Overridden',
        zhTitle: 'Chinese title',
        zhContent: 'Chinese body',
        enTitle: 'English title',
        enContent: 'English body',
        save: 'Save notification templates',
        saved: 'Notification templates saved',
      },
      system: {
        title: 'System Maintenance',
        maintenanceMode: 'Maintenance mode',
        maintenanceModeDesc: 'When enabled, normal users cannot access the site; admins remain allowed',
        backupTitle: 'Data backup',
        generating: 'Generating...',
        downloadDatabase: 'Download database',
        exporting: 'Exporting...',
        exportLogs: 'Export logs',
        processing: 'Processing...',
        clearCache: 'Clear cache',
        backupGenerated: 'Data backup generated',
        logsExported: 'Audit logs exported',
        cacheRefreshed: 'Cache refreshed',
        dangerTitle: '⚠️ Dangerous operation',
        dangerDescription: 'Production does not provide one-click database clearing in the page. To reset data, download a backup first, then have an administrator run a controlled script on the server.',
        saveConfig: 'Save configuration',
      },
    },
  },
};

const AdminLanguageContext = createContext(null);

export function normalizeAdminLanguage(value) {
  return SUPPORTED_ADMIN_LANGUAGES.includes(value) ? value : 'zh';
}

function readStoredLanguage() {
  if (typeof window === 'undefined' || !window.localStorage) return 'zh';
  return normalizeAdminLanguage(window.localStorage.getItem(ADMIN_LANGUAGE_STORAGE_KEY));
}

export function AdminLanguageProvider({ children }) {
  const [language, setLanguageState] = useState(readStoredLanguage);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(ADMIN_LANGUAGE_STORAGE_KEY, language);
    }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language === 'zh' ? 'zh-CN' : 'en';
    }
  }, [language]);

  const value = useMemo(() => ({
    language,
    copy: adminTranslations[language],
    setLanguage: (nextLanguage) => setLanguageState(normalizeAdminLanguage(nextLanguage)),
  }), [language]);

  return (
    <AdminLanguageContext.Provider value={value}>
      {children}
    </AdminLanguageContext.Provider>
  );
}

export function useAdminLanguage() {
  const context = useContext(AdminLanguageContext);
  if (!context) {
    return {
      language: 'zh',
      copy: adminTranslations.zh,
      setLanguage: () => {},
    };
  }
  return context;
}
